import React from "react";
import PropTypes from "prop-types";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import styles from "./components.module.scss";
import { BorderColorOutlined } from "@mui/icons-material";
import DeleteIcon from "../../Assets/DeleteIcon-blue.svg";

const AttendanceAttributesTable = ({
  title,
  buttonLabel,
  rows,
  loading,
  columns,
  onAdd,
  onEdit,
  onDelete,
  disabled,
}) => {
  // Filter columns to exclude Actions column when disabled is false
  const visibleColumns = !disabled
    ? columns
    : columns.filter((col) => col.variant !== "actions");
  const renderRequirementBadge = (requirement) => {
    if (requirement === undefined || requirement === null) {
      return "-";
    }
    const isMandatory =
      typeof requirement === "string"
        ? requirement.toLowerCase() === "mandatory"
        : Boolean(requirement);

    const label =
      typeof requirement === "string"
        ? requirement
        : isMandatory
        ? "Mandatory"
        : "Optional";

    return (
      <span
        className={`${styles.badge} ${isMandatory ? styles.mandatory : ""}`}
      >
        {label}
      </span>
    );
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {!disabled && (
          <button className={styles.addButton} type="button" onClick={onAdd}>
            <AddIcon fontSize="small" />
            {buttonLabel}
          </button>
        )}
      </div>
      <div className={styles.card}>
        {loading ? (
          <div className={styles.loaderContainer}>
            <CircularProgress size={32} />
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  {visibleColumns.map((column) => {
                    const isAttributeNameColumn = column.id === "attributeName";
                    return (
                      <th
                        key={column.id || column.header}
                        className={`${styles.th} ${
                          isAttributeNameColumn
                            ? styles.attributeNameHeader
                            : ""
                        }`}
                        style={{ textAlign: column.align || "center" }}
                      >
                        {column.header}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={styles.tbody}>
                {rows?.length ? (
                  rows.map((row) => {
                    const rowKey = row.id || row.attributeName;
                    return (
                      <tr key={rowKey}>
                        {visibleColumns.map((column) => {
                          const accessorValue =
                            typeof column.accessor === "function"
                              ? column.accessor(row)
                              : row[column.id];

                          let cellContent = accessorValue ?? "-";

                          if (column.render) {
                            cellContent = column.render(row, accessorValue);
                          } else if (column.variant === "badge") {
                            cellContent = accessorValue ? (
                              <span className={styles.badge}>
                                {accessorValue}
                              </span>
                            ) : (
                              "-"
                            );
                          } else if (column.variant === "requirement") {
                            cellContent = renderRequirementBadge(accessorValue);
                          } else if (column.variant === "actions") {
                            cellContent = (
                              <div className={styles.actions}>
                                <IconButton
                                  size="small"
                                  style={{ color: "#1C4980" }}
                                  onClick={() => onEdit?.(row)}
                                  disabled={disabled}
                                >
                                  <BorderColorOutlined fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => onDelete?.(row)}
                                  disabled={disabled}
                                >
                                  <img
                                    src={DeleteIcon}
                                    alt="Delete"
                                    style={{ width: 20, height: 20 }}
                                  />
                                </IconButton>
                              </div>
                            );
                          }

                          // Format attributeName column with 2-line truncation and hover tooltip
                          let displayContent = cellContent || "-";
                          const isAttributeNameColumn =
                            column.id === "attributeName";

                          if (
                            isAttributeNameColumn &&
                            typeof cellContent === "string" &&
                            cellContent !== "-"
                          ) {
                            displayContent = (
                              <div className={styles.attributeNameContent}>
                                <Tooltip
                                  title={cellContent}
                                  enterDelay={0}
                                  leaveDelay={0}
                                  arrow
                                >
                                  <span>{cellContent}</span>
                                </Tooltip>
                              </div>
                            );
                          }

                          return (
                            <td
                              key={column.id || column.header}
                              className={`${styles.td} ${
                                !column.align ? styles.center : ""
                              }`}
                              style={{
                                textAlign: column.align || "center",
                                maxWidth: isAttributeNameColumn
                                  ? "190px"
                                  : `${100 / visibleColumns.length}%`,
                              }}
                            >
                              {displayContent}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      className={styles.emptyStateCell}
                    >
                      <div className={styles.emptyState}>
                        No attributes found. Click "+ Add Attribute" to create
                        one.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

AttendanceAttributesTable.propTypes = {
  title: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      header: PropTypes.string.isRequired,
      dataKey: PropTypes.string,
      align: PropTypes.oneOf(["left", "center", "right"]),
      accessor: PropTypes.func,
      render: PropTypes.func,
      variant: PropTypes.oneOf(["badge", "requirement", "actions"]),
    })
  ),
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  disabled: PropTypes.bool,
};

AttendanceAttributesTable.defaultProps = {
  buttonLabel: "Add",
  rows: [],
  loading: false,
  columns: [],
  onAdd: () => {},
  onEdit: () => {},
  onDelete: () => {},
  disabled: false,
};

export default AttendanceAttributesTable;
